/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import { toast } from 'react-toastify';
import { getApi, postApi } from 'views/services/api';

const HOUR_SUGGESTIONS = [3, 4, 6, 12];

export default function StepStayTypes({ hotelId, onNext, onBack }) {
  const theme = useTheme();
  const [existing, setExisting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [offerPartial, setOfferPartial] = useState(false);
  const [partialRows, setPartialRows] = useState([]);

  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await getApi(`api/hotel/stay-types/list/${hotelId}`);
        if (res?.status === 200 && Array.isArray(res.data)) {
          setExisting(res.data);
          if (res.data.some((s) => !s.isNightly)) setOfferPartial(true);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchExisting();
  }, [hotelId]);

  const hasNightly = existing.some((s) => s.isNightly);

  const addPartialRow = (hours = '') =>
    setPartialRows((prev) => [...prev, { label: hours ? `${hours} Horas` : '', hours: hours || '' }]);

  const addSuggestedHours = (h) => {
    const label = `${h} Horas`;
    const already =
      partialRows.some((r) => r.label.toLowerCase() === label.toLowerCase()) ||
      existing.some((s) => s.label.toLowerCase() === label.toLowerCase());
    if (already) return;
    addPartialRow(h);
  };

  const updatePartialRow = (index, field, value) => {
    setPartialRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const updated = { ...r, [field]: value };
        // Si cambia las horas y el label era autogenerado, actualizarlo
        if (field === 'hours' && (!r.label || /^\d+ Horas$/.test(r.label))) {
          updated.label = value ? `${value} Horas` : '';
        }
        return updated;
      })
    );
  };

  const removePartialRow = (index) => setPartialRows((prev) => prev.filter((_, i) => i !== index));

  const handleNext = async () => {
    const validPartials = offerPartial
      ? partialRows
          .map((r) => ({ label: r.label.trim(), hours: parseInt(r.hours, 10) || 0 }))
          .filter((r) => r.label && r.hours > 0)
      : [];

    setSaving(true);
    try {
      // 1. "Por Noche" siempre debe existir
      if (!hasNightly) {
        const res = await postApi('api/hotel/stay-types/add', {
          hotelId,
          label: 'Por Noche',
          hours: 24,
          isNightly: true
        });
        if (res?.status !== 200) {
          toast.error('No se pudo crear la estancia "Por Noche"');
          setSaving(false);
          return;
        }
      }

      // 2. Estancias parciales nuevas (sin duplicar las existentes)
      for (const p of validPartials) {
        const dup = existing.some((s) => s.label.trim().toLowerCase() === p.label.toLowerCase());
        if (dup) continue;
        const res = await postApi('api/hotel/stay-types/add', {
          hotelId,
          label: p.label,
          hours: p.hours,
          isNightly: false
        });
        if (res?.status !== 200) {
          toast.error(`No se pudo crear la estancia "${p.label}"`);
          setSaving(false);
          return;
        }
      }

      toast.success('Tipos de estancia guardados');
      onNext();
    } catch (e) {
      console.error(e);
      toast.error('Error guardando tipos de estancia');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Tipos de estancia
        </Typography>
        <Typography variant="body2" color="text.secondary">
          La estancia por noche viene incluida. Si tu hotel también ofrece estancias por horas (tiempo parcial), actívalas aquí.
        </Typography>
      </Box>

      {/* Por Noche (fija) */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}
      >
        <NightsStayIcon color="primary" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Por Noche
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Check-in y check-out por fechas. Es la estancia estándar de todo hotel.
          </Typography>
        </Box>
        <Chip label={hasNightly ? 'Ya configurada' : 'Se creará'} color="primary" size="small" />
      </Box>

      {existing.filter((s) => !s.isNightly).length > 0 && (
        <Alert severity="info">
          Estancias por horas ya configuradas: {existing.filter((s) => !s.isNightly).map((s) => s.label).join(', ')}
        </Alert>
      )}

      <FormControlLabel
        control={<Switch checked={offerPartial} onChange={(e) => setOfferPartial(e.target.checked)} />}
        label="¿Ofreces estancias por horas (tiempo parcial)?"
      />

      {offerPartial && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {HOUR_SUGGESTIONS.map((h) => (
              <Chip key={h} label={`${h} horas`} onClick={() => addSuggestedHours(h)} clickable variant="outlined" color="primary" />
            ))}
          </Stack>

          {partialRows.map((row, i) => (
            <Grid container spacing={2} key={i} alignItems="center">
              <Grid item xs={6} sm={7}>
                <TextField
                  fullWidth
                  label="Nombre"
                  placeholder="Ej: 4 Horas"
                  value={row.label}
                  onChange={(e) => updatePartialRow(i, 'label', e.target.value)}
                />
              </Grid>
              <Grid item xs={4} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Horas"
                  inputProps={{ min: 1, max: 23 }}
                  value={row.hours}
                  onChange={(e) => updatePartialRow(i, 'hours', e.target.value)}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton onClick={() => removePartialRow(i)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Box>
            <Button startIcon={<AddIcon />} onClick={() => addPartialRow()} sx={{ textTransform: 'none' }}>
              Agregar estancia por horas
            </Button>
          </Box>
        </Stack>
      )}

      <Divider />

      <Stack direction="row" justifyContent="space-between">
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: 'none' }} disabled={saving}>
          Atrás
        </Button>
        <Button variant="contained" onClick={handleNext} disabled={saving} sx={{ px: 4, fontWeight: 600 }}>
          {saving ? 'Guardando...' : 'Continuar'}
        </Button>
      </Stack>
    </Stack>
  );
}
